import { Fade, Perspective } from '@egjs/flicking-plugins';
import EgjsFlicking, {
  FlickingOptions,
  FlickingProps as EgjsFlickingProps,
  ViewportSlot,
  VisibleChangeEvent,
  ChangedEvent
} from '@egjs/react-flicking';
import React, { CSSProperties, useMemo, useRef } from 'react';
import styled from 'styled-components';

type FlickingProps = {
  width?: CSSProperties['width'];
  list: string[];
  defaultIndex: number;
  borderColor?: CSSProperties['borderColor'];
  bgColor?: CSSProperties['backgroundColor'];
  wrapperProps?: Omit<React.HTMLProps<HTMLDivElement>, 'as' | 'ref'>;
  onChange?: (v: string) => void;
};

export function Flicking({
  width = '100%',
  list,
  defaultIndex,
  bgColor = 'transparent',
  borderColor = 'transparent',
  wrapperProps = {},
  onChange
}: FlickingProps) {
  const options: Partial<FlickingOptions & EgjsFlickingProps> = useMemo(
    () => ({
      plugins: [
        new Fade(),
        new Perspective({ rotate: -1, scale: 1.2, perspective: 50 })
      ],
      circular: false,
      align: 'center',
      horizontal: false,
      panelsPerView: 5
    }),
    []
  );

  const onChanged = (e: ChangedEvent<EgjsFlicking>) => {
    if (onChange) onChange(e.panel.element.innerText);
  };

  return (
    <Wrapper
      width={width}
      bgColor={bgColor}
      borderColor={borderColor}
      {...wrapperProps}
    >
      <EgjsFlicking
        className="date-flicking"
        horizontal={false}
        defaultIndex={defaultIndex}
        onChanged={onChanged}
        {...options}
      >
        {list.map(v => (
          <div key={v} className="date-panel">
            {v}
          </div>
        ))}
        <ViewportSlot>
          <div className="date-panel-border"></div>
          <div className="shadow-top"></div>
          <div className="shadow-bottom"></div>
        </ViewportSlot>
      </EgjsFlicking>
    </Wrapper>
  );
}

const Wrapper = styled.div<{
  width: FlickingProps['width'];
  borderColor: CSSProperties['borderColor'];
  bgColor: CSSProperties['backgroundColor'];
}>`
  overflow-y: hidden;
  border-radius: 0.5rem;
  &:first-of-type .date-panel-border {
    border-radius: 8px 0 0 8px;
  }
  &:last-of-type .date-panel-border {
    border-radius: 0px 8px 8px 0px;
  }

  .date-flicking {
    width: 100%;
    overflow: visible;
    box-sizing: content-box;
    height: 150px;
    perspective: 400px;
    transform-style: preserve-3d;
    background-color: ${props => props.bgColor};
  }
  .date-panel {
    width: ${props => props.width};
    height: 30px;
    backface-visibility: hidden;
    background-color: ${props => props.bgColor};
    color: black;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: ${({ theme }) => theme.palette.gray15};
  }
  .date-panel-border {
    width: 100%;
    height: 30px;
    position: absolute;
    z-index: 0;
    top: 60px;
    border-top: 2px solid ${props => props.borderColor};
    border-bottom: 2px solid ${props => props.borderColor};
    background-color: ${({ theme }) => theme.palette.gray600};
  }
  .shadow-top {
    width: 100%;
    height: 48px;
    position: absolute;
    top: 0;
    left: 0;
    background-color: ${props => props.bgColor};
  }
  .shadow-bottom {
    width: 100%;
    height: 48px;
    position: absolute;
    top: 72px;
    left: 0;
    background-color: ${props => props.bgColor};
  }
`;
